import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Book } from '../api/types';
import { useUser } from '../state/UserContext';
import { ALL_TRADITIONS, TRADITION_LABELS, type Tradition } from '../theme/tokens';

export function Library() {
  const { me } = useUser();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Tradition | 'all'>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getLibrary(filter === 'all' ? undefined : filter)
      .then((res) => {
        if (!cancelled) setBooks(res.books);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the library.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filter]);

  const handleDownload = async (bookId: string) => {
    setDownloadingId(bookId);
    setError(null);
    try {
      const res = await api.getLibraryDownload(bookId);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not get a download link.');
    } finally {
      setDownloadingId(null);
    }
  };

  const filterOptions: Array<Tradition | 'all'> = ['all', ...(me?.traditions.length ? me.traditions : ALL_TRADITIONS)];

  return (
    <div>
      <h1 className="page-title">Library</h1>
      <p className="page-subtitle">Public-domain texts across traditions.</p>

      <div className="filter-row">
        {filterOptions.map((f) => (
          <button
            key={f}
            type="button"
            className={`filter-chip${filter === f ? ' selected' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : TRADITION_LABELS[f]}
          </button>
        ))}
      </div>

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : books.length === 0 ? (
        <div className="empty-state">No texts found for this filter.</div>
      ) : (
        <div className="library-list">
          {books.map((b) => (
            <div key={b.bookId} className="library-item">
              <div>
                <div className="library-item-title">{b.title}</div>
                <div className="library-item-meta">
                  {TRADITION_LABELS[b.tradition] ?? b.tradition} &middot; {b.format.toUpperCase()} &middot; {b.license}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleDownload(b.bookId)}
                disabled={downloadingId === b.bookId}
              >
                {downloadingId === b.bookId ? 'Opening...' : 'Download'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
